import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import api from "@/lib/api";
import SiteHeader from "@/components/SiteHeader";
import SiteFooter from "@/components/SiteFooter";

export default function ConsignorDetailPage() {
  const { slug } = useParams();
  const [c, setC] = useState(null);
  const [settings, setSettings] = useState(null);
  const [notFound, setNotFound] = useState(false);
  const [others, setOthers] = useState([]);

  useEffect(() => {
    (async () => {
      try {
        const [detail, st, all] = await Promise.all([
          api.get(`/consignors/${slug}`),
          api.get("/site-settings"),
          api.get("/consignors"),
        ]);
        setC(detail.data);
        setSettings(st.data);
        setOthers(all.data.filter((x) => x.slug !== slug).slice(0, 4));
      } catch (e) {
        setNotFound(true);
      }
    })();
  }, [slug]);

  if (notFound) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ background: "var(--bg-primary)" }}>
        <div className="text-center">
          <div className="font-display text-6xl mb-4">Consignor not found</div>
          <Link to="/consignors" className="link-brass">Back to all consignors →</Link>
        </div>
      </div>
    );
  }
  if (!c) return <div data-testid="consignor-detail-loading" className="min-h-screen" style={{ background: "var(--bg-primary)" }} />;

  return (
    <div data-testid={`consignor-detail-${slug}`} className="min-h-screen" style={{ background: "var(--bg-primary)" }}>
      <SiteHeader />

      {/* Hero */}
      <section className="relative min-h-[72vh] flex items-end overflow-hidden">
        <div className="absolute inset-0">
          <img src={c.hero_image_url || c.image_url} alt={c.name} className="w-full h-full object-cover" />
        </div>
        <div className="absolute inset-0 hero-overlay" />
        <div className="relative w-full max-w-[1400px] mx-auto px-6 md:px-12 pb-20 pt-32">
          <Link to="/consignors" className="eyebrow-muted text-[11px] mb-8 inline-block" style={{ color: "rgba(255,255,255,0.7)" }}>
            ← All Consignors
          </Link>
          <div className="eyebrow reveal" style={{ color: "#E8D7A8" }}>{c.location}</div>
          <h1
            data-testid="consignor-title"
            className="font-display text-6xl md:text-8xl lg:text-[9rem] mt-5 leading-[0.95] reveal reveal-delay-1"
            style={{ color: "#FFFFFF", textShadow: "0 4px 24px rgba(14,44,70,0.55)" }}
          >
            {c.name}
          </h1>
          {c.short_tagline && (
            <div className="font-italic-serif text-2xl md:text-4xl mt-4 reveal reveal-delay-2" style={{ color: "#E8D7A8" }}>
              {c.short_tagline}
            </div>
          )}
        </div>
      </section>

      {/* Family & Contact */}
      <section className="py-20 md:py-28">
        <div className="max-w-[1400px] mx-auto px-6 md:px-12 grid grid-cols-1 md:grid-cols-12 gap-12">
          <div className="md:col-span-4">
            {c.family_photo_url && (
              <div data-testid="consignor-family-card">
                <div className="frame-image overflow-hidden" style={{ aspectRatio: "4 / 3" }}>
                  <img
                    src={c.family_photo_url}
                    alt={c.family_names || c.name}
                    className="w-full h-full object-cover"
                    style={{ objectPosition: "center top" }}
                  />
                </div>
                <div className="eyebrow-muted text-[10px] mt-3" style={{ color: "var(--brass)" }}>The Family</div>
                {c.family_names && (
                  <div className="font-display text-lg leading-snug mt-1" style={{ color: "var(--text-primary)" }}>
                    {c.family_names}
                  </div>
                )}
              </div>
            )}
          </div>
          <div className="md:col-span-8">
            <div className="eyebrow mb-6">Contact</div>
            {c.description && (
              <p className="font-display text-3xl md:text-5xl leading-[1.1] mb-10" style={{ color: "var(--text-primary)" }}>
                {c.description}
              </p>
            )}
            <div className="border p-8 md:p-10" style={{ borderColor: "var(--border-subtle)", background: "var(--bg-secondary)" }}>
              <div className="font-display text-2xl md:text-3xl leading-tight" style={{ color: "var(--brass-deep)" }}>
                {c.name}
              </div>
              {c.family_names && (
                <div className="font-italic-serif text-lg mt-1" style={{ color: "var(--text-secondary)" }}>
                  {c.family_names}
                </div>
              )}
              <dl className="mt-8 space-y-5">
                {c.address && (
                  <div className="flex flex-col sm:flex-row sm:gap-8">
                    <dt className="eyebrow text-[10px] sm:w-24 shrink-0 pt-1">Address</dt>
                    <dd className="text-base" data-testid="consignor-address" style={{ color: "var(--text-primary)" }}>{c.address}</dd>
                  </div>
                )}
                {c.contact_phone && (
                  <div className="flex flex-col sm:flex-row sm:gap-8">
                    <dt className="eyebrow text-[10px] sm:w-24 shrink-0 pt-1">Phone</dt>
                    <dd className="text-base" data-testid="consignor-phone" style={{ color: "var(--text-primary)" }}>
                      {c.contact_phone.split("•").map((p, i) => {
                        const digits = p.replace(/[^\d]/g, "");
                        return (
                          <span key={p.trim()}>
                            {i > 0 && <span className="mx-2" style={{ color: "var(--brass)" }}>•</span>}
                            <a href={`tel:${digits}`} className="link-brass">{p.trim()}</a>
                          </span>
                        );
                      })}
                    </dd>
                  </div>
                )}
                {c.contact_email && (
                  <div className="flex flex-col sm:flex-row sm:gap-8">
                    <dt className="eyebrow text-[10px] sm:w-24 shrink-0 pt-1">Email</dt>
                    <dd className="text-base">
                      <a href={`mailto:${c.contact_email}`} data-testid="consignor-email" className="link-brass">
                        {c.contact_email}
                      </a>
                    </dd>
                  </div>
                )}
                {c.website && (
                  <div className="flex flex-col sm:flex-row sm:gap-8">
                    <dt className="eyebrow text-[10px] sm:w-24 shrink-0 pt-1">Website</dt>
                    <dd className="text-base">
                      <a href={c.website} target="_blank" rel="noopener noreferrer" data-testid="consignor-website" className="link-brass">
                        Visit ranch →
                      </a>
                    </dd>
                  </div>
                )}
              </dl>
            </div>
            {c.story && (
              <p className="mt-10 text-base md:text-lg max-w-3xl leading-relaxed whitespace-pre-line" style={{ color: "var(--text-secondary)" }}>
                {c.story}
              </p>
            )}
          </div>
        </div>
      </section>

      {/* Gallery */}
      {c.gallery && c.gallery.length > 0 && (
        <section className="px-6 md:px-12 pb-20">
          <div className="max-w-[1400px] mx-auto">
            <div className="eyebrow mb-8">Gallery</div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {c.gallery.map((g, i) => (
                <div key={g} className="frame-image" style={{ aspectRatio: "4 / 5" }}>
                  <img src={g} alt={`${c.name} ${i + 1}`} className="w-full h-full object-cover" />
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* CTA to sale */}
      <section className="border-t border-b" style={{ borderColor: "var(--border-subtle)" }}>
        <div className="max-w-[1400px] mx-auto px-6 md:px-12 py-16 md:py-20 flex flex-col md:flex-row md:items-center justify-between gap-8">
          <div>
            <div className="eyebrow mb-3">Next Sale</div>
            <div className="font-display text-3xl md:text-5xl leading-tight">
              Legacy Sale  •  December 5, 2026<br />
              <span className="text-2xl md:text-3xl" style={{ color: "var(--text-secondary)" }}>
                Billings Livestock Commission
              </span>
            </div>
          </div>
          <div className="flex flex-col sm:flex-row gap-3">
            <Link to="/sale/legacy" className="btn-primary">View Legacy Sale →</Link>
            <Link to="/sale/annual" className="btn-ghost">2027 Annual</Link>
          </div>
        </div>
      </section>

      {/* Other consignors */}
      {others.length > 0 && (
        <section className="py-24 md:py-32">
          <div className="max-w-[1400px] mx-auto px-6 md:px-12">
            <div className="eyebrow mb-8">More Consignors</div>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 md:gap-5">
              {others.map((o) => (
                <Link key={o.id} to={`/consignor/${o.slug}`} className="frame-image relative block group" style={{ aspectRatio: "3 / 4" }}>
                  <img src={o.family_photo_url || o.image_url} alt={o.name} className="w-full h-full object-cover" style={{ objectPosition: "center top" }} />
                  <div className="absolute inset-0" style={{ background: "linear-gradient(to top, rgba(7,11,20,0.95), rgba(7,11,20,0.2) 60%, transparent)" }} />
                  <div className="absolute inset-x-0 bottom-0 p-4 md:p-6">
                    <div className="eyebrow text-[10px] mb-1" style={{ color: "var(--brass-bright)" }}>{o.location}</div>
                    <div className="font-display text-lg md:text-2xl leading-tight">{o.name}</div>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}

      <SiteFooter settings={settings} />
    </div>
  );
}
